import type { AppKennung, Benachrichtigung, EinstellungsAenderung, EreignisEinstellung, Kanal, Zustellart } from './typen';

/** App-Namen im Text (U7): „Finanzen“, nicht „FIN“. */
export const APP_NAME: Record<AppKennung, string> = {
  crm: 'CRM',
  pm: 'PM',
  finance: 'Finanzen',
  buchhaltung: 'Buchhaltung',
  konto: 'Konto',
};

export const KANAL_NAME: Record<string, string> = {
  in_app: 'In der App',
  email: 'Per Mail',
};

export type Ziel = { art: 'intern'; pfad: string } | { art: 'extern'; href: string } | null;

/**
 * Wohin ein Eintrag führt. In derselben App per Router (`intern`), in einer
 * anderen per Seitenwechsel (`extern`), ohne Ziel gar nicht.
 *
 * Erst die Herkunft der absoluten Adresse (gleiche Domain heißt gleiche App,
 * auch für Altmeldungen ohne Ereignis), dann die App-Kennung: In der
 * Entwicklung zeigen die absoluten Adressen auf die Produktion, der Pfad
 * gehört aber trotzdem in die laufende App.
 */
/** Ein Pfad in der App: beginnt mit genau einem „/“, ohne Backslash und Steuerzeichen (sonst `//fremd.example`). */
function sichererPfad(pfad: string | null | undefined): pfad is string {
  // eslint-disable-next-line no-control-regex
  return !!pfad && /^\/(?![/\\])/.test(pfad) && !/[\\\u0000-\u001f\u007f]/.test(pfad);
}

export function ziel(eintrag: Pick<Benachrichtigung, 'app' | 'action_path' | 'action_href'>, app: AppKennung, origin: string): Ziel {
  if (eintrag.action_href) {
    try {
      const url = new URL(eintrag.action_href);
      const pfad = `${url.pathname}${url.search}${url.hash}`;
      if (url.origin === origin && sichererPfad(pfad)) return { art: 'intern', pfad };
    } catch {
      // keine gültige Adresse, weiter mit dem Pfad
    }
  }
  if (eintrag.app === app && sichererPfad(eintrag.action_path)) return { art: 'intern', pfad: eintrag.action_path };
  if (eintrag.action_href && /^https?:\/\//.test(eintrag.action_href)) return { art: 'extern', href: eintrag.action_href };
  return null;
}

// Einstellungen: je Kanal „Aus“ und „An“, ein Kanal, der sammeln kann, statt
// „An“ jede Zustellart. Übernommen aus Konto (account-web), dort ungetestet.

export const AUS = 'off';
export const AN = 'on';

export interface Wahl {
  value: string;
  label: string;
}

export function wahlen(kanal: Kanal, zustellarten: Zustellart[]): Wahl[] {
  const aus = { value: AUS, label: 'Aus' };
  if (!kanal.bundling) return [aus, { value: AN, label: 'An' }];
  return [aus, ...zustellarten.map((z) => ({ value: z.key, label: z.label }))];
}

/** Der gewählte Knopf eines Ereignisses in einem Kanal. */
export function aktuelleWahl(ereignis: EreignisEinstellung, kanal: Kanal): string {
  const stand = ereignis.channels[kanal.key];
  if (!stand?.enabled) return AUS;
  return kanal.bundling ? stand.mode : AN;
}

/** Ist dieser Kanal für das Ereignis immer an (Pflicht)? */
export function istPflicht(ereignis: EreignisEinstellung, kanal: Kanal): boolean {
  return ereignis.pflicht_kanaele?.includes(kanal.key) ?? false;
}

/**
 * Einheitlicher Stand einer Kategorie in einem Kanal, oder `null`, wenn gemischt.
 * Pflichtereignisse zählen nicht mit: „Alle: Aus“ lässt sie an, und die Kategorie
 * soll danach trotzdem „Aus“ zeigen.
 */
export function kategorieWahl(ereignisse: EreignisEinstellung[], kanal: Kanal): string | null {
  const waehlbar = ereignisse.filter((e) => !istPflicht(e, kanal));
  if (waehlbar.length === 0) return aktuelleWahl(ereignisse[0], kanal);
  const werte = new Set(waehlbar.map((e) => aktuelleWahl(e, kanal)));
  return werte.size === 1 ? [...werte][0] : null;
}

/** Aus dem gedrückten Knopf die Änderung für den Server. */
export function wahlAlsAenderung(wahl: string): EinstellungsAenderung {
  if (wahl === AUS) return { enabled: false };
  if (wahl === AN) return { enabled: true };
  return { enabled: true, mode: wahl };
}

/**
 * Die Änderung schon vor der Antwort im Stand zeigen (optimistisch).
 * Ein Pflichtkanal bleibt an: Der Server lehnt das Abschalten ab bzw. überspringt
 * es bei „Alle: Aus“, der Stand soll nicht kurz „Aus“ zeigen.
 */
export function mitAenderung(
  ereignisse: EreignisEinstellung[],
  betroffen: (e: EreignisEinstellung) => boolean,
  kanal: string,
  aenderung: EinstellungsAenderung,
): EreignisEinstellung[] {
  return ereignisse.map((e) => {
    if (!betroffen(e)) return e;
    if (!aenderung.enabled && e.pflicht_kanaele?.includes(kanal)) return e;
    const alt = e.channels[kanal] ?? { enabled: false, mode: 'instant' };
    return { ...e, channels: { ...e.channels, [kanal]: { enabled: aenderung.enabled, mode: aenderung.mode ?? alt.mode } } };
  });
}
